import { useEffect, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { FileText, ExternalLink } from 'lucide-react';

interface BusinessDocumentPreviewProps {
  /** 사업자등록증 파일 URL. null/undefined 면 "미제출" 안내를 노출. */
  documentUrl: string | null | undefined;
  /** 로딩 중(상세 조회 등)일 때 스켈레톤 대신 안내 텍스트를 노출. */
  loading?: boolean;
}

/** URL 확장자로 이미지 여부 판별(쿼리스트링 제거 후 판정). presign URL 의 query 는 무시. */
const isImageUrl = (url: string): boolean => {
  const path = url.split('?')[0]?.toLowerCase() ?? '';
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(path);
};

/**
 * B2B(2026-06-20) D4 — 사업자등록증 미리보기.
 * 이미지면 인라인 썸네일, 그 외(PDF 등)는 새 탭 링크 + 파일 아이콘으로 노출한다.
 */
export const BusinessDocumentPreview = ({ documentUrl, loading }: BusinessDocumentPreviewProps) => {
  const { t } = useTranslation('common');
  // QA-381 — 이미지가 열리지 않으면(비공개 S3 주소 403 등) 깨진 그림 대신 안내 + 원본 링크.
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [documentUrl]);

  if (loading) {
    return (
      <p className="text-xs" style={{ color: 'var(--content-muted)' }}>
        {t('businessMembers.document.loading', '서류 정보를 불러오는 중...')}
      </p>
    );
  }

  if (!documentUrl) {
    return (
      <p
        className="rounded-md px-3 py-2 text-xs"
        style={{ background: 'var(--surface-1)', color: 'var(--content-muted)' }}
      >
        {t('businessMembers.document.none', '제출된 사업자등록증이 없습니다.')}
      </p>
    );
  }

  if (broken) {
    return (
      <div
        role="alert"
        className="space-y-1 rounded-md px-3 py-2 text-xs"
        style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        data-testid="business-document-unavailable"
      >
        <p>{t('businessMembers.document.unavailable', '사업자등록증 파일을 열 수 없습니다. 서버에서 파일 조회 권한을 확인해야 합니다.')}</p>
        <a href={documentUrl} target="_blank" rel="noopener noreferrer" className="underline">
          {t('businessMembers.document.openFile', '사업자등록증 보기')}
        </a>
      </div>
    );
  }

  return isImageUrl(documentUrl) ? (
    <a
      href={documentUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="block overflow-hidden rounded-md border"
      style={{ borderColor: 'var(--divider-strong)' }}
      aria-label={t('businessMembers.document.openImage', '사업자등록증 원본 열기')}
    >
      <img
        src={documentUrl}
        alt={t('businessMembers.document.alt', '사업자등록증')}
        className="max-h-48 w-full object-contain"
        onError={() => setBroken(true)}
        style={{ background: 'var(--surface-1)' }}
      />
    </a>
  ) : (
    <a
      href={documentUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm"
      style={{
        borderColor: 'var(--divider-strong)',
        background: 'var(--surface-1)',
        color: 'var(--content-strong)',
      }}
    >
      <FileText size={16} aria-hidden />
      <span>{t('businessMembers.document.openFile', '사업자등록증 보기')}</span>
      <ExternalLink size={14} aria-hidden style={{ color: 'var(--content-muted)' }} />
    </a>
  );
};
